import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BRAND_MONOGRAM, BRAND_WORDMARK_VAZADA } from "@/lib/brand";

const NEWSROOM_IMG =
  "https://images.unsplash.com/photo-1753153481105-7a979eabe5a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBuZXdzcm9vbSUyMGJyb2FkY2FzdCUyMGRlc2t8ZW58MHx8fHwxNzgyNTE0Mjc0fDA&ixlib=rb-4.1.0&q=85";

const formatError = (d) => {
  if (!d) return "Erro inesperado.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d.msg || String(d);
};

export default function LoginPage() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Bem-vindo(a)!");
        nav("/");
      } else {
        await register(email, password, name);
        setPending(true);
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // ------- MOBILE SHELL: fullscreen photo background + glass form overlay -------
  const MobileShell = ({ children }) => (
    <div
      className="lg:hidden fixed inset-0 overflow-y-auto bg-zinc-950"
      data-testid="login-mobile-shell"
      style={{ minHeight: "100dvh" }}
    >
      <img
        src={NEWSROOM_IMG}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover opacity-80"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/50 via-zinc-950/40 to-zinc-950/95" />
      <div className="relative min-h-full flex flex-col p-6 gap-4">
        {/* Hero: brand mark (compact, no vertical whitespace around the logo) */}
        <div className="flex flex-col items-start" data-testid="login-mobile-hero">
          <div className="text-[10px] uppercase tracking-[0.35em] font-bold text-[#E8431E] mb-1">
            PodiUmScript · Editorial OS
          </div>
          <div
            className="w-56 max-w-[70%] overflow-hidden"
            style={{ aspectRatio: "2 / 1" }}
          >
            <img
              src={BRAND_WORDMARK_VAZADA}
              alt="PodiUmScript"
              className="w-full h-auto invert drop-shadow-2xl"
              style={{ marginTop: "-25%", marginBottom: "-25%" }}
            />
          </div>
        </div>

        {/* Glass form card */}
        <div>{children}</div>

        {/* Tagline pinned to the bottom */}
        <p className="mt-auto text-sm text-zinc-200 italic max-w-xs">
          Espelho, retrancas e teleprompter — tudo num só lugar.
        </p>
      </div>
    </div>
  );

  // ------- DESKTOP SHELL: two-column -------
  const DesktopShell = ({ children, side }) => (
    <div className="hidden lg:grid min-h-screen grid-cols-2 bg-white" data-testid="login-desktop-shell">
      <div className="relative bg-zinc-900">
        <img
          src={NEWSROOM_IMG}
          alt="Newsroom"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-zinc-950/80 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#E8431E]">
            PodiUmScript · Editorial OS
          </div>
          <div>
            <img
              src={BRAND_WORDMARK_VAZADA}
              alt="PodiUmScript"
              className="w-full max-w-md drop-shadow-2xl invert"
            />
            <p className="mt-6 max-w-md text-zinc-300 italic">
              Espelho, retrancas e teleprompter — tudo num só lugar.
            </p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">{side}</div>
    </div>
  );

  // ------- Pending approval screen -------
  if (pending) {
    const PendingCard = ({ dark = false }) => (
      <div
        className={`w-full max-w-sm space-y-5 ${dark ? "rounded-sm p-6 bg-black/60 backdrop-blur-xl border border-white/15 text-white" : ""}`}
        data-testid={dark ? "pending-approval-mobile" : "pending-approval"}
      >
        <div className={`text-[11px] uppercase tracking-[0.3em] font-bold ${dark ? "text-amber-400" : "text-amber-600"}`}>
          Cadastro recebido
        </div>
        <h2 className={`font-heading text-3xl tracking-tight font-bold ${dark ? "text-white" : "text-zinc-950"}`}>
          Aguarde a aprovação
        </h2>
        <p className={`leading-relaxed ${dark ? "text-zinc-300" : "text-zinc-600"}`}>
          Seu cadastro foi enviado ao responsável. Assim que ele aprovar, você poderá entrar com seu email e senha.
        </p>
        <Button
          data-testid={dark ? "back-to-login-btn-mobile" : "back-to-login-btn"}
          onClick={() => { setPending(false); setMode("login"); }}
          className="w-full h-11 rounded-sm bg-[#152D4B] hover:bg-[#0E1F36] text-white"
        >
          Voltar ao login
        </Button>
      </div>
    );
    return (
      <>
        <MobileShell><PendingCard dark /></MobileShell>
        <DesktopShell side={<PendingCard />} />
      </>
    );
  }

  // ------- Login/Register form (shared markup, variant via `dark`) -------
  const Form = ({ dark = false }) => {
    const labelCls = `text-xs uppercase tracking-wider ${dark ? "text-zinc-300" : ""}`;
    const inputCls = dark
      ? "rounded-sm h-11 bg-white/10 border-white/20 text-white placeholder:text-zinc-400 focus-visible:ring-2 focus-visible:ring-[#E8431E] focus-visible:border-[#E8431E]"
      : "rounded-sm border-zinc-300 h-11 focus-visible:ring-2 focus-visible:ring-[#152D4B]";
    return (
      <form
        onSubmit={submit}
        className={`w-full max-w-sm space-y-5 ${dark ? "rounded-sm p-6 bg-black/55 backdrop-blur-xl border border-white/15 shadow-2xl" : ""}`}
        data-testid={dark ? "login-form-mobile" : "login-form"}
      >
        <div>
          <div className={`text-[11px] uppercase tracking-[0.3em] font-bold mb-3 flex items-center gap-2 ${dark ? "text-zinc-300" : "text-zinc-500"}`}>
            <img src={BRAND_MONOGRAM} alt="PodiUmScript" className={`h-6 w-auto ${dark ? "invert brightness-200" : ""}`} />
            <span>{mode === "login" ? "Acesso" : "Nova conta"}</span>
          </div>
          <h2 className={`font-heading text-2xl sm:text-3xl tracking-tight font-bold ${dark ? "text-white" : "text-zinc-950"}`}>
            {mode === "login" ? "Entrar no painel" : "Criar uma conta"}
          </h2>
        </div>

        {mode === "register" && (
          <div className="space-y-2">
            <Label htmlFor={`name-${dark ? "m" : "d"}`} className={labelCls}>Nome</Label>
            <Input
              id={`name-${dark ? "m" : "d"}`}
              data-testid={dark ? "register-name-mobile" : "register-name"}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className={inputCls}
            />
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor={`email-${dark ? "m" : "d"}`} className={labelCls}>Email</Label>
          <Input
            id={`email-${dark ? "m" : "d"}`}
            type="email"
            data-testid={dark ? "login-email-mobile" : "login-email"}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoCapitalize="none"
            autoComplete="email"
            className={inputCls}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`password-${dark ? "m" : "d"}`} className={labelCls}>Senha</Label>
          <Input
            id={`password-${dark ? "m" : "d"}`}
            type="password"
            data-testid={dark ? "login-password-mobile" : "login-password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            className={inputCls}
          />
        </div>

        <Button
          type="submit"
          data-testid={dark ? "login-submit-button-mobile" : "login-submit-button"}
          disabled={loading}
          className={`w-full h-11 rounded-sm text-white text-sm tracking-wide font-semibold ${dark ? "bg-[#E8431E] hover:bg-[#C73815]" : "bg-[#152D4B] hover:bg-[#0E1F36]"}`}
        >
          {loading ? "Aguarde..." : mode === "login" ? "Entrar" : "Cadastrar"}
        </Button>

        <button
          type="button"
          data-testid={dark ? "toggle-auth-mode-mobile" : "toggle-auth-mode"}
          onClick={() => setMode(mode === "login" ? "register" : "login")}
          className={`block text-sm mx-auto underline underline-offset-4 ${dark ? "text-zinc-200 hover:text-white" : "text-zinc-600 hover:text-[#152D4B]"}`}
        >
          {mode === "login" ? "Não tem conta? Cadastre-se" : "Já tenho conta — entrar"}
        </button>
      </form>
    );
  };

  return (
    <>
      <MobileShell><Form dark /></MobileShell>
      <DesktopShell side={<Form />} />
    </>
  );
}
